import React from "react";
import { AlertTriangle, CheckCircle2, Download, FileArchive, Loader2, XCircle } from "lucide-react";
import translations from "../common/Translations";
import CommonButton from "../common/CommonButton";
import ModalCloseButton from "../common/ModalCloseButton";
import FailedAccountsPanel from "./FailedAccountsPanel";
import { BULK_PDF_EXPORT_PHASE } from "../../hooks/useBulkPdfExport";
import {
  BULK_EXPORT_STATUS,
  clampPercentage,
  fillTemplate,
  formatBulkExportDateRange,
  formatBulkExportTimestamp,
  formatFileSize,
  getBulkExportStatusMeta,
  openBulkExportFile,
} from "./bulkExportUtils";

const StatTile = ({ label, value, valueClass = "text-slate-800" }) => (
  <div className="rounded-md bg-slate-50 px-1.5 py-1 text-center">
    <p className="truncate text-[9px] font-semibold uppercase tracking-[0.04em] text-slate-400">
      {label}
    </p>
    <p className={`text-xs font-bold leading-tight ${valueClass}`}>{value}</p>
  </div>
);

const InfoRow = ({ label, value }) => (
  <div className="flex items-baseline justify-between gap-2 py-[2px]">
    <span className="shrink-0 text-[10px] font-semibold uppercase tracking-[0.04em] text-slate-400">
      {label}
    </span>
    <span className="min-w-0 truncate text-right text-[11px] font-medium text-slate-700 rtl:text-left">
      {value}
    </span>
  </div>
);

/**
 * Live progress for the export the user just queued (or deep-linked to from a
 * completion notification), driven by the bulk-export status endpoint
 * (BLF-2171).
 *
 * Split in two: the left half is the progress bar and the outcome action, the
 * right half is the total/succeeded/failed counters plus the details of what
 * was exported. Pending renders an indeterminate bar rather than a 0%-wide
 * one, because "queued, not started" otherwise reads as stuck.
 */
const BulkExportProgressCard = ({ phase, record, message, langMode, onDismiss, canDownload = true }) => {
  const t = translations[langMode] ?? {};
  if (!record && !message) return null;

  const status = record?.status;
  const statusMeta = getBulkExportStatusMeta(status, langMode);
  const percentage = clampPercentage(record?.progressPercentage);
  const processedCount = Number(record?.processedCount) || 0;
  const totalCount = Number(record?.totalCount) || 0;
  const failedCount = Number(record?.failedCount) || 0;
  const accountNoCount =
    Number(record?.accountNoCount) ||
    (Array.isArray(record?.accountNos) ? record.accountNos.length : 0);
  const fileSize = formatFileSize(record?.fileSizeBytes);

  const isQueued = status === BULK_EXPORT_STATUS.PENDING;
  const isCompleted = phase === BULK_PDF_EXPORT_PHASE.COMPLETED;
  const isFailed = phase === BULK_PDF_EXPORT_PHASE.FAILED;
  const isUnreachable = phase === BULK_PDF_EXPORT_PHASE.ERROR;
  const isRunning = phase === BULK_PDF_EXPORT_PHASE.TRACKING;

  return (
    <div className="rounded-xl border border-[#236DB4]/30 bg-blue-50/40 p-2.5">
      <div className="flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2">
          <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-white text-[#236DB4]">
            <FileArchive size={15} />
          </span>
          <p className="truncate text-xs font-bold text-slate-800">
            {`${t.bulkExportIdLabel ?? "Export ID"} #${record?.exportId ?? "-"}`}
          </p>
          <span
            className={`inline-flex shrink-0 items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold ${statusMeta.badgeClass}`}
          >
            {isRunning && <Loader2 size={10} className="animate-spin" />}
            {statusMeta.label}
          </span>
        </div>
        {onDismiss && (
          <ModalCloseButton
            onClick={onDismiss}
            size={14}
            ariaLabel={t.bulkExportDismiss ?? "Dismiss"}
            className="h-7 w-7 shrink-0"
          />
        )}
      </div>

      <div className="mt-2 grid grid-cols-1 gap-2 lg:grid-cols-2">
        {/* Part one -- progress, and whatever action the outcome calls for. */}
        <div className="flex min-w-0 flex-col justify-between gap-2 rounded-lg border border-slate-200 bg-white p-2.5">
          <div className="space-y-1.5">
            <div className="flex items-center justify-between gap-2">
              <p className="truncate text-[11px] font-semibold text-slate-700">
                {isCompleted
                  ? t.bulkExportCompletedHeading ?? "Your ZIP is ready"
                  : isFailed
                    ? t.bulkExportFailedHeading ?? "Export failed"
                    : isQueued
                      ? t.bulkExportQueuedHeading ?? "Queued — waiting for a worker to pick it up"
                      : t.bulkExportInProgressHeading ?? "Preparing PDFs"}
              </p>
              {!isQueued && (
                <span className="shrink-0 text-[11px] font-bold text-[#236DB4]">{percentage}%</span>
              )}
            </div>

            <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
              {isQueued && isRunning ? (
                <div className="animate-bulk-export-indeterminate h-full w-1/3 rounded-full bg-[#236DB4]/70" />
              ) : (
                <div
                  className={`h-full rounded-full ${statusMeta.barClass} transition-[width] duration-500 ease-out`}
                  style={{ width: `${percentage}%` }}
                />
              )}
            </div>

            <p className="truncate text-[10px] text-slate-500">
              {isQueued && isRunning
                ? t.bulkExportQueuedHelp ?? "Your export is in the queue and will start shortly."
                : fillTemplate(t.bulkExportProcessedOfTotal ?? "{processed} of {total} PDFs", {
                    processed: processedCount,
                    total: totalCount,
                  })}
            </p>
          </div>

          {isCompleted && (
            <div className="flex flex-col gap-1.5 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex min-w-0 items-center gap-1.5">
                <CheckCircle2 size={13} className="shrink-0 text-emerald-600" />
                <p className="truncate text-[10px] text-emerald-700">
                  {fileSize ||
                    (t.bulkExportCompletedHelp ?? "Download it below or from Export History.")}
                </p>
              </div>
              <CommonButton colorIntent="primary"
                variant="primary"
                type="button"
                disabled={!record?.fileUrl || !canDownload}
                onClick={() => openBulkExportFile(record?.fileUrl)}
                tooltipText={t.bulkExportDownloadZipTooltip ?? "Download the prepared ZIP"}
                tooltipPosition="top"
                className="inline-flex h-8 min-h-[32px] w-full items-center justify-center gap-1.5 text-[11px] font-medium sm:w-auto sm:min-w-[130px]"
              >
                <Download size={13} className="shrink-0" />
                <span className="truncate">{t.bulkExportDownloadZip ?? "Download ZIP"}</span>
              </CommonButton>
            </div>
          )}

          {isFailed && (
            <p className="flex items-start gap-1.5 rounded-md bg-red-50 px-2 py-1.5 text-[10px] text-red-700">
              <XCircle size={12} className="mt-0.5 shrink-0" />
              <span className="break-words">
                {message ||
                  record?.errorMessage ||
                  (t.bulkExportFailedHelp ?? "The export could not be completed. Please try again.")}
              </span>
            </p>
          )}

          {isUnreachable && (
            <p className="flex items-start gap-1.5 rounded-md bg-amber-50 px-2 py-1.5 text-[10px] text-amber-800">
              <AlertTriangle size={12} className="mt-0.5 shrink-0" />
              <span className="break-words">
                {message ||
                  (t.bulkExportErrorHelp ??
                    "Progress updates stopped. The export may still be running — check Export History.")}
              </span>
            </p>
          )}
        </div>

        {/* Part two -- the counters, then what this export actually covers. */}
        <div className="min-w-0 rounded-lg border border-slate-200 bg-white p-2.5">
          <div className="grid grid-cols-3 gap-1.5">
            <StatTile label={t.bulkExportTotalLabel ?? "Total"} value={totalCount} />
            <StatTile
              label={t.bulkExportSuccessLabel ?? "Succeeded"}
              value={Number(record?.successCount) || 0}
              valueClass="text-emerald-600"
            />
            <StatTile
              label={t.bulkExportFailedLabel ?? "Failed"}
              value={failedCount}
              valueClass={failedCount > 0 ? "text-amber-600" : "text-slate-800"}
            />
          </div>

          <div className="mt-2 border-t border-slate-100 pt-1.5">
            <InfoRow
              label={t.bulkExportActivityLabel ?? "Activity"}
              value={record?.workflowName || record?.workflowId || "-"}
            />
            <InfoRow
              label={t.dateRange ?? "Date Range"}
              value={
                <span className="rtl-keep-ltr">
                  {formatBulkExportDateRange(record?.fromDate, record?.toDate)}
                </span>
              }
            />
            <InfoRow
              label={t.accno ?? "Account No"}
              value={
                accountNoCount > 0
                  ? fillTemplate(t.bulkExportSelectedAccounts ?? "{count} selected", {
                      count: accountNoCount,
                    })
                  : t.bulkExportAllAccountsInRange ?? "All accounts in the date range"
              }
            />
            <InfoRow
              label={t.bulkExportRequestedAt ?? "Requested"}
              value={
                <span className="rtl-keep-ltr">
                  {formatBulkExportTimestamp(record?.requestedAt)}
                </span>
              }
            />
            {record?.completedAt && (
              <InfoRow
                label={t.bulkExportCompletedAt ?? "Completed"}
                value={
                  <span className="rtl-keep-ltr">
                    {formatBulkExportTimestamp(record?.completedAt)}
                  </span>
                }
              />
            )}
            {fileSize && <InfoRow label={t.bulkExportFileSize ?? "Size"} value={fileSize} />}
          </div>
        </div>
      </div>

      {(isCompleted || isFailed) && (
        <div className="mt-2">
          <FailedAccountsPanel
            failedCount={failedCount}
            failedAccountNos={record?.failedAccountNos}
            exportId={record?.exportId}
            langMode={langMode}
          />
        </div>
      )}
    </div>
  );
};

export default BulkExportProgressCard;
